import { today } from "@repo/helpers";
import { KeyedArray } from "@repo/models";
import { Col, EditableGridPage, Loading } from "@repo/ui";
import { useCallback, useMemo } from "react";

import { useBackgroundCheckQueries } from "../../api/backgroundCheckApi";
import { useRepSegmentsApi } from "../../api/repSegmentsApi";
import { SingleOnboardingCompletion } from "../../components/onboardingCompletion";
import { BackgroundCheckProvider, useBackgroundCheckContext, useRepContext } from "../../contexts/editableItemContext";
import { useOnboardingContext } from "../../contexts/onboardingContext";
import { useSegmentContext } from "../../contexts/segmentContext";
import { usePermissionsCheck } from "../../helpers/permissions";
import { useEntityId, useUpdateRoute } from "../../helpers/urlHooks";
import { BackgroundCheck } from "../../models";
import BackgroundCheckEditor from "./editor";
import BackgroundCheckGridBasic from "./grid";
import { Props } from "./types";

function BackgroundChecks(props: Props) {
	return (
		<BackgroundCheckProvider>
			<BackgroundCheckInternal {...props} />
		</BackgroundCheckProvider>
	);
}

function BackgroundCheckInternal(props: Props) {
	const { repId } = useRepContext();
	const { sessionId } = useOnboardingContext();
	const { segmentId } = useSegmentContext();
	const { overwriteItem, pendingChanges } = useBackgroundCheckContext();
	const { useRepSegmentsQuery } = useRepSegmentsApi(repId);
	const { useBackgroundCheckQuery } = useBackgroundCheckQueries();
	const { loading: segmentsLoading } = useRepSegmentsQuery();
	const { data: backgroundChecks, loaded: backgroundChecksLoaded } = useBackgroundCheckQuery();
	const entityId = useEntityId();
	const updateRoute = useUpdateRoute();

	const { fullAPReadOnly } = usePermissionsCheck();

	const inSegment = useCallback(
		({ onboardingSegmentId }: BackgroundCheck) => {
			if (!segmentId) return true;
			if (segmentId === onboardingSegmentId) return true;
			if (!onboardingSegmentId) return true;
			return false;
		},
		[segmentId],
	);

	const inSessionOrActive = useCallback(
		({ onboardingSessionId }: BackgroundCheck) => {
			if (!sessionId) return true;
			if (sessionId === onboardingSessionId) return true;
			return false;
		},
		[sessionId],
	);

	const filteredBackgroundChecks = useMemo(() => {
		if (!backgroundChecks?.any()) return new KeyedArray<BackgroundCheck>([], (b) => b.backgroundCheckId);
		return backgroundChecks.filter(inSegment).filter(inSessionOrActive);
	}, [backgroundChecks, inSegment, inSessionOrActive]);

	const handleOpen = useCallback(
		(item: BackgroundCheck) => {
			overwriteItem(item);
		},
		[overwriteItem],
	);

	const newItem = useCallback(() => {
		overwriteItem({
			requestDate: today(),
			representativeId: repId,
		} as BackgroundCheck);
	}, [overwriteItem, repId]);

	return (
		<Loading loading={segmentsLoading}>
			<Col md={12} tag="section" id="onboarding">
				<BackgroundCheckCompletion />
			</Col>
			<EditableGridPage<BackgroundCheck>
				id="BackgroundChecksContainer"
				items={filteredBackgroundChecks}
				keyProperty="backgroundCheckId"
				Editor={BackgroundCheckEditor}
				Grid={BackgroundCheckGridBasic}
				openItem={handleOpen}
				pendingChanges={pendingChanges}
				newItem={newItem}
				itemsLoaded={backgroundChecksLoaded}
				readOnly={props.readOnly}
				entityId={entityId}
				updateRoute={updateRoute}
				fullReadOnly={fullAPReadOnly}
			/>
		</Loading>
	);
}

function BackgroundCheckCompletion() {
	return <SingleOnboardingCompletion req={"bgCheck"} key={"bgCheck"} depthToDisplay={0} />;
}

export default BackgroundChecks;
